/**
 * PricingBanner - Upgrade promotion banner shown on company dashboard
 * when the company exceeds the free tier limit.
 */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const Banner = styled.div`
  background: linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(217,119,6,0.10) 50%, rgba(251,191,36,0.08) 100%);
  border: 1px solid #D97706;
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  padding: 20px 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const BannerIcon = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #F59E0B, #D97706);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #fff;
`;

const BannerContent = styled.div`
  flex: 1;
  min-width: 200px;
`;

const BannerTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin-bottom: 4px;
`;

const BannerText = styled.div`
  font-size: 0.82rem;
  color: ${(p) => p.theme.colors.textSecondary};
  line-height: 1.5;
`;

const UpgradeButton = styled.button`
  padding: 10px 24px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: linear-gradient(135deg, #F59E0B, #D97706);
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover { opacity: 0.9; transform: translateY(-1px); }
`;

function PricingBanner({ slug, activeMembers, isAdmin }) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  if (!isAdmin || activeMembers <= 3) return null;

  return (
    <Banner>
      <BannerIcon>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </BannerIcon>
      <BannerContent>
        <BannerTitle>
          {t('Proプランにアップグレード', 'Upgrade to Pro Plan')}
        </BannerTitle>
        <BannerText>
          {t(
            `現在${activeMembers}人のメンバーがいます。4人目以降は¥490/人/月で利用できます。AIチャット回数の拡大や組織AI分析もご利用いただけます。`,
            `You have ${activeMembers} members. Additional members beyond 3 cost ¥490/person/month. Unlock more AI chat and org analysis features.`
          )}
        </BannerText>
      </BannerContent>
      <UpgradeButton onClick={() => navigate(`/company/${slug}/settings?tab=billing`)}>
        {t('アップグレード', 'Upgrade')}
      </UpgradeButton>
    </Banner>
  );
}

export default PricingBanner;
